import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileText, Calendar, Zap, AlertCircle, Trash2, Mail, RefreshCw, Send, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComparisonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: '', name: '', message: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [showRecompare, setShowRecompare] = useState(false);
  const [tariffs, setTariffs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [recompareTariff, setRecompareTariff] = useState('');
  const [recomparePlan, setRecomparePlan] = useState('');
  const [recomparing, setRecomparing] = useState(false);
  const [outcomeValue, setOutcomeValue] = useState('');
  const [outcomeReason, setOutcomeReason] = useState('');
  const [outcomeSaving, setOutcomeSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/comparisons/${id}?authorization=${token}`);
        setComparison(res.data);
        setOutcomeValue(res.data.outcome || 'pending');
        setOutcomeReason(res.data.outcome_reason || '');
      } catch (err) {
        toast.error('Failed to load comparison');
        navigate('/history');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, token, navigate]);

  const downloadReport = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/comparisons/${id}/report?authorization=${token}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NBE_Comparison_${comparison.report_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded!');
    } catch (err) { toast.error('Failed to download report'); }
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/comparisons/${id}/export?format=csv&authorization=${token}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comparison_${id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported!');
    } catch (err) { toast.error('Failed to export CSV'); }
  };

  const sendEmailToCustomer = async () => {
    if (!emailForm.email) { toast.error('Please enter customer email'); return; }
    setEmailSending(true);
    try {
      const form = new FormData();
      form.append('customer_email', emailForm.email);
      form.append('customer_name', emailForm.name);
      form.append('message', emailForm.message);
      await axios.post(`${API_URL}/api/comparisons/${id}/send-email?authorization=${token}`, form);
      toast.success(`Comparison emailed to ${emailForm.email}`);
      setShowEmailDialog(false);
      // Refresh to show quote_status
      const res = await axios.get(`${API_URL}/api/comparisons/${id}?authorization=${token}`);
      setComparison(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally { setEmailSending(false); }
  };

  const openRecompare = async () => {
    try {
      const [t, p] = await Promise.all([
        axios.get(`${API_URL}/api/tariffs?authorization=${token}`),
        axios.get(`${API_URL}/api/plans?authorization=${token}`)
      ]);
      setTariffs(t.data);
      setPlans(p.data);
      setRecompareTariff(comparison?.tariff_id || '');
      setRecomparePlan(comparison?.plan_id || '');
      setShowRecompare(true);
    } catch { toast.error('Failed to load tariffs/plans'); }
  };

  const handleRecompare = async () => {
    if (!recompareTariff) { toast.error('Please select a tariff'); return; }
    setRecomparing(true);
    try {
      const form = new FormData();
      form.append('tariff_id', recompareTariff);
      form.append('plan_id', recomparePlan);
      const res = await axios.post(`${API_URL}/api/comparisons/${id}/recompare?authorization=${token}`, form);
      toast.success('Re-comparison created!');
      navigate(`/comparison/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-compare failed');
    } finally { setRecomparing(false); }
  };

  const handleEmailComparison = async () => {
    // First download the PDF
    try {
      const res = await axios.get(`${API_URL}/api/comparisons/${id}/report?authorization=${token}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NBE_Comparison_${comparison.report_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('PDF download failed:', err);
    }

    // Build email content
    const d = comparison.edited_data || comparison.extracted_data || {};
    const r = comparison.comparison_result || {};
    const subject = `Next Business Energy - Bill Comparison for ${d.customer_name || 'Customer'} (${comparison.report_id})`;
    const body = [
      `Hi,`,
      ``,
      `Please find attached the electricity bill comparison report for:`,
      ``,
      `Customer: ${d.customer_name || 'N/A'}`,
      `NMI: ${d.nmi || 'N/A'}`,
      `Site: ${d.site_address || 'N/A'}`,
      ``,
      `--- Summary ---`,
      `Current Retailer Total: ${formatCurrency(r.retailer_total)}`,
      `NBE Offer Total: ${formatCurrency(r.our_total)}`,
      `Estimated Savings: ${formatCurrency(r.savings_amount)} (${(r.savings_percentage || 0).toFixed(1)}%)`,
      `Estimated Annual Savings: ${formatCurrency((r.savings_amount || 0) * 4)}`,
      ``,
      `Report ID: ${comparison.report_id}`,
      ``,
      `Please attach the downloaded PDF report to this email.`,
      ``,
      `Kind regards,`,
      `${comparison.created_by_name}`,
      `Next Business Energy`,
    ].join('\n');

    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    toast.success('PDF downloaded. Please attach it to the email.');
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/comparisons/${id}?authorization=${token}`);
      toast.success('Comparison deleted');
      navigate('/history');
    } catch (err) { toast.error('Failed to delete comparison'); }
  };

  const saveOutcome = async (val) => {
    setOutcomeValue(val);
    setOutcomeSaving(true);
    try {
      const form = new FormData();
      form.append('outcome', val);
      form.append('outcome_reason', outcomeReason);
      await axios.put(`${API_URL}/api/comparisons/${id}/outcome?authorization=${token}`, form);
      toast.success('Outcome updated');
    } catch (err) { toast.error('Failed to update outcome'); }
    finally { setOutcomeSaving(false); }
  };

  const saveOutcomeReason = async () => {
    setOutcomeSaving(true);
    try {
      const form = new FormData();
      form.append('outcome', outcomeValue);
      form.append('outcome_reason', outcomeReason);
      await axios.put(`${API_URL}/api/comparisons/${id}/outcome?authorization=${token}`, form);
      toast.success('Reason saved');
    } catch (err) { toast.error('Failed to save reason'); }
    finally { setOutcomeSaving(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A';
  const formatCurrency = (a) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(a || 0);

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div></div></Layout>;
  }

  if (!comparison) return null;

  const data = comparison.edited_data || comparison.extracted_data || {};
  const result = comparison.comparison_result || {};
  const assumptions = result.assumptions || [];
  const retailerBreakdown = result.retailer_breakdown || [];
  const nbeBreakdown = result.nbe_breakdown || [];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="comparison-detail-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="rounded-full"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-900">Comparison Report</h1>
                <Badge variant="outline" className="font-mono">{comparison.report_id}</Badge>
              </div>
              <p className="text-slate-500 text-sm mt-1">Created by {comparison.created_by_name} on {formatDate(comparison.created_at)}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowEmailDialog(true)} data-testid="send-to-customer-btn"><Send className="w-4 h-4 mr-2" />Send to Customer</Button>
            <Button variant="outline" onClick={openRecompare} data-testid="recompare-btn"><RefreshCw className="w-4 h-4 mr-2" />Re-compare</Button>
            <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">Export CSV</Button>
            <Button variant="outline" onClick={handleEmailComparison} data-testid="email-comparison-btn"><Mail className="w-4 h-4 mr-2" />Email</Button>
            <Button onClick={downloadReport} className="bg-primary hover:bg-primary/90" data-testid="download-pdf-btn"><Download className="w-4 h-4 mr-2" />Download PDF</Button>
            <Button variant="ghost" size="icon" onClick={() => setShowDelete(true)} className="text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="delete-comparison-btn"><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>

        <Card className="gradient-success overflow-hidden">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-green-900 font-medium">Potential Savings</p>
                <p className="text-5xl font-bold text-green-900 mt-1" data-testid="total-savings">{formatCurrency(result.savings_amount)}</p>
                <p className="text-green-800 mt-1">{(result.savings_percentage || 0).toFixed(1)}% less than current retailer</p>
              </div>
              <div className="text-right">
                <p className="text-green-900 font-medium">Estimated Annual Savings</p>
                <p className="text-3xl font-bold text-green-900 mt-1">{formatCurrency((result.savings_amount || 0) * 4)}</p>
                <p className="text-green-700 text-sm">Based on quarterly billing</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <span className="text-sm font-medium text-slate-700">Outcome:</span>
                <Select value={outcomeValue} onValueChange={saveOutcome} disabled={outcomeSaving}>
                  <SelectTrigger className="w-[140px]" data-testid="outcome-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
                {outcomeValue === 'lost' && (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={outcomeReason} onChange={(e) => setOutcomeReason(e.target.value)} placeholder="Reason for loss..." className="h-9" data-testid="outcome-reason" />
                    <Button size="sm" variant="outline" onClick={saveOutcomeReason} disabled={outcomeSaving} data-testid="save-reason-btn">Save</Button>
                  </div>
                )}
              </div>
              {comparison.source === 'customer_portal' && <Badge className="bg-purple-100 text-purple-700">Customer Portal Lead</Badge>}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-primary" />Customer Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between"><span className="text-slate-500">Customer Name</span><span className="font-medium">{data.customer_name || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">NMI</span><span className="font-mono">{data.nmi || 'N/A'}</span></div>
              <div className="flex justify-between items-start"><span className="text-slate-500">Site Address</span><span className="font-medium text-right max-w-[60%]">{data.site_address || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Current Retailer</span><span className="font-medium">{data.retailer_name || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Supply Days</span><span className="font-medium">{data.supply_days || 30} days</span></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-primary" />Usage Summary</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between"><span className="text-slate-500">Total Usage</span><span className="font-medium">{(data.total_kwh || 0).toFixed(2)} kWh</span></div>
              {data.peak_kwh > 0 && <div className="flex justify-between"><span className="text-slate-500">Peak Usage</span><span className="font-medium">{data.peak_kwh.toFixed(2)} kWh</span></div>}
              {data.shoulder_kwh > 0 && <div className="flex justify-between"><span className="text-slate-500">Shoulder Usage</span><span className="font-medium">{data.shoulder_kwh.toFixed(2)} kWh</span></div>}
              {data.off_peak_kwh > 0 && <div className="flex justify-between"><span className="text-slate-500">Off-Peak Usage</span><span className="font-medium">{data.off_peak_kwh.toFixed(2)} kWh</span></div>}
              <div className="flex justify-between"><span className="text-slate-500">Original Bill Total</span><span className="font-semibold">{formatCurrency(data.total_bill_amount)}</span></div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Breakdowns */}
        {(retailerBreakdown.length > 0 || nbeBreakdown.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="bg-slate-50 border-b">
                <CardTitle className="text-lg">Current Retailer Breakdown</CardTitle>
                <CardDescription>Line-by-line calculation from bill</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {retailerBreakdown.map((item, idx) => (
                    <div key={idx} className={`p-4 ${item.category === 'Total' ? 'bg-slate-100 font-semibold' : ''} ${item.category === 'Discounts' ? 'text-green-700' : ''}`}>
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-sm text-slate-600">{item.description}</span>
                        <span className={`font-mono ${item.amount < 0 ? 'text-green-600' : ''}`}>
                          {item.amount < 0 ? '-' : ''}{formatCurrency(Math.abs(item.amount))}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 font-mono">{item.formula}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="border-primary/30">
              <CardHeader className="bg-primary/5 border-b border-primary/20">
                <CardTitle className="text-lg text-primary">NBE Proposed Breakdown</CardTitle>
                <CardDescription>Detailed calculation with tariff & plan</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {nbeBreakdown.map((item, idx) => (
                    <div key={idx} className={`p-4 ${item.category === 'Total' ? 'bg-primary/10 font-semibold' : ''} ${item.category === 'Discounts' ? 'text-green-700' : ''}`}>
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-sm text-slate-600">{item.description}</span>
                        <span className={`font-mono ${item.amount < 0 ? 'text-green-600' : ''}`}>
                          {item.amount < 0 ? '-' : ''}{formatCurrency(Math.abs(item.amount))}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 font-mono">{item.formula}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Summary Table */}
        <Card>
          <CardHeader><CardTitle>Cost Summary</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Charge Type</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Current Retailer</th>
                    <th className="text-right py-3 px-4 font-semibold text-primary">Next Business Energy</th>
                    <th className="text-right py-3 px-4 font-semibold text-green-600">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b"><td className="py-3 px-4">Supply Charges</td><td className="text-right py-3 px-4">{formatCurrency(result.retailer_supply_cost)}</td><td className="text-right py-3 px-4">{formatCurrency(result.our_supply_cost)}</td><td className="text-right py-3 px-4 text-green-600">{formatCurrency((result.retailer_supply_cost || 0) - (result.our_supply_cost || 0))}</td></tr>
                  <tr className="border-b"><td className="py-3 px-4">Usage Charges</td><td className="text-right py-3 px-4">{formatCurrency(result.retailer_usage_cost)}</td><td className="text-right py-3 px-4">{formatCurrency(result.our_usage_cost)}</td><td className="text-right py-3 px-4 text-green-600">{formatCurrency((result.retailer_usage_cost || 0) - (result.our_usage_cost || 0))}</td></tr>
                  <tr className="border-b"><td className="py-3 px-4">Demand Charges</td><td className="text-right py-3 px-4">{formatCurrency(result.retailer_demand_cost)}</td><td className="text-right py-3 px-4">{formatCurrency(result.our_demand_cost)}</td><td className="text-right py-3 px-4 text-green-600">{formatCurrency((result.retailer_demand_cost || 0) - (result.our_demand_cost || 0))}</td></tr>
                  <tr className="border-b"><td className="py-3 px-4">Discounts Applied</td><td className="text-right py-3 px-4 text-green-600">-{formatCurrency(result.retailer_discount)}</td><td className="text-right py-3 px-4 text-green-600">-{formatCurrency(result.our_discount)}</td><td className="text-right py-3 px-4">-</td></tr>
                  {(result.retailer_solar_credit > 0 || result.our_solar_credit > 0) && (
                    <tr className="border-b"><td className="py-3 px-4">Solar Feed-in Credit</td><td className="text-right py-3 px-4 text-green-600">-{formatCurrency(result.retailer_solar_credit)}</td><td className="text-right py-3 px-4 text-green-600">-{formatCurrency(result.our_solar_credit)}</td><td className="text-right py-3 px-4 text-green-600">{formatCurrency((result.retailer_solar_credit || 0) - (result.our_solar_credit || 0))}</td></tr>
                  )}
                  {result.our_greenpower_cost > 0 && (
                    <tr className="border-b"><td className="py-3 px-4">GreenPower 100%</td><td className="text-right py-3 px-4">-</td><td className="text-right py-3 px-4">{formatCurrency(result.our_greenpower_cost)}</td><td className="text-right py-3 px-4">-</td></tr>
                  )}
                  {result.our_metering_cost > 0 && (
                    <tr className="border-b"><td className="py-3 px-4">Metering Charge</td><td className="text-right py-3 px-4">-</td><td className="text-right py-3 px-4">{formatCurrency(result.our_metering_cost)}</td><td className="text-right py-3 px-4">-</td></tr>
                  )}
                  <tr className="bg-slate-100 font-semibold"><td className="py-4 px-4">Total</td><td className="text-right py-4 px-4">{formatCurrency(result.retailer_total)}</td><td className="text-right py-4 px-4 text-primary">{formatCurrency(result.our_total)}</td><td className="text-right py-4 px-4 text-green-600">{formatCurrency(result.savings_amount)}</td></tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {assumptions.length > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardHeader className="pb-2"><CardTitle className="text-amber-800 text-base flex items-center gap-2"><AlertCircle className="w-5 h-5" />Assumptions & Notes</CardTitle></CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-1 text-sm text-amber-900">
                {assumptions.map((a, i) => <li key={i}>{a}</li>)}
                <li>This comparison is based on the information extracted from your bill and may vary based on actual usage patterns.</li>
                <li>All prices include GST unless otherwise stated.</li>
              </ul>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2"><FileText className="w-4 h-4" /><span>File: {comparison.bill_file_name}</span></div>
              <div className="flex items-center gap-2"><Zap className="w-4 h-4" /><span>Plan: {comparison.rate_sheet_name}</span></div>
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>Created: {formatDate(comparison.created_at)}</span></div>
            </div>
          </CardContent>
        </Card>
        {comparison.quote_status && (
          <Card className={comparison.quote_status === 'accepted' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}>
            <CardContent className="p-4 flex items-center gap-3">
              <Send className={`w-5 h-5 ${comparison.quote_status === 'accepted' ? 'text-green-600' : 'text-blue-600'}`} />
              <div>
                <p className={`font-medium ${comparison.quote_status === 'accepted' ? 'text-green-800' : 'text-blue-800'}`}>
                  Quote {comparison.quote_status === 'accepted' ? 'Accepted by Customer' : 'Sent to Customer'}
                </p>
                <p className="text-xs text-slate-500">
                  {comparison.customer_email && `Sent to ${comparison.customer_email}`}
                  {comparison.quote_accepted_at && ` | Accepted ${new Date(comparison.quote_accepted_at).toLocaleDateString('en-AU')}`}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Comparison</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete comparison {comparison.report_id}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-comparison">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Send to Customer Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Send Comparison to Customer</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Customer Email *</Label><Input value={emailForm.email} onChange={e => setEmailForm({...emailForm, email: e.target.value})} placeholder="customer@example.com" data-testid="customer-email-input" /></div>
              <div><Label>Customer Name</Label><Input value={emailForm.name} onChange={e => setEmailForm({...emailForm, name: e.target.value})} placeholder="John Smith" /></div>
              <div><Label>Message (optional)</Label><Input value={emailForm.message} onChange={e => setEmailForm({...emailForm, message: e.target.value})} placeholder="Personal message to include in email" /></div>
              <p className="text-xs text-slate-500">The comparison PDF will be attached. Customer will receive an "Accept Quote" button.</p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowEmailDialog(false)} className="flex-1">Cancel</Button>
                <Button onClick={sendEmailToCustomer} disabled={emailSending} className="flex-1 bg-primary hover:bg-primary/90" data-testid="send-email-btn">
                  {emailSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Send
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Re-compare Dialog */}
        <Dialog open={showRecompare} onOpenChange={setShowRecompare}>
          <DialogContent>
            <DialogHeader><DialogTitle>Quick Re-compare</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Re-run this comparison with a different tariff or plan. A new comparison will be created.</p>
              <div>
                <Label>Tariff *</Label>
                <Select value={recompareTariff} onValueChange={setRecompareTariff}>
                  <SelectTrigger data-testid="recompare-tariff-select"><SelectValue placeholder="Select tariff" /></SelectTrigger>
                  <SelectContent>{tariffs.map(t => <SelectItem key={t.id} value={t.id}>{t.tariff_code} - {t.tariff_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Plan</Label>
                <Select value={recomparePlan} onValueChange={setRecomparePlan}>
                  <SelectTrigger data-testid="recompare-plan-select"><SelectValue placeholder="No plan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Plan</SelectItem>
                    {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.plan_name} ({p.discount_percentage}%)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowRecompare(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleRecompare} disabled={recomparing} className="flex-1 bg-primary hover:bg-primary/90" data-testid="run-recompare-btn">
                  {recomparing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}Re-compare
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
